import { Category } from '@fcom/dapi/api/models';

export const BOUND_BASED_CATEGORIES = [
  Category.BAGGAGE,
  Category.SPORT,
  Category.PET,
  Category.CABIN_BAGGAGE,
  Category.SAF,
];
export const isBoundBasedCategory = (category: Category): boolean => BOUND_BASED_CATEGORIES.includes(category);
export const isJourneyBasedCategory = (category: Category): boolean => [Category.COVER].includes(category);

export const SEAT_BUNDLE_CATEGORIES: Category[] = [
  Category.SEAT_WIFI,
  Category.SEAT_CABIN_BAGGAGE,
  Category.SEAT_MEAL,
  Category.SEAT_PRIORITY,
];

/**
 * Ancillary+ancillary bundles (a.k.a. non-seat bundles). Unlike the seat bundles above, these do
 * not rely on seat selection and get their own selection view.
 */
export const ANCILLARY_BUNDLE_CATEGORIES: Category[] = [
  Category.LOUNGE_CABIN_BAGGAGE,
  Category.LOUNGE_WIFI,
  Category.LOUNGE_PRIORITY,
  Category.WIFI_MEAL,
];

export const isAncillaryBundle = (category: Category): boolean => ANCILLARY_BUNDLE_CATEGORIES.includes(category);

const ALL_CATEGORIES = Object.values(Category);

/**
 * Returns the related categories for a given category. E.g. SEAT_MEAL => [SEAT, MEAL]
 * Will return either the category itself (if no overlaps) or an array of categories the given category consists of
 * @param category - the category we look the overlaps for
 */
const getRelatedCategoriesForCategory = (category: Category | null | undefined): Category[] => {
  if (!category) {
    return [];
  }

  let baseCategories: Category[] = [];
  for (const prefixCategory of ALL_CATEGORIES) {
    // If the category is base category or does not start with another category, move on
    if (prefixCategory === category || !category.startsWith(prefixCategory)) {
      continue;
    }

    // Check if category is suffix part of a bundle, otherwise 'specialNeed' and such would look like a bundle by accident.
    const suffix = category.slice(prefixCategory.length);
    const suffixCategory = ALL_CATEGORIES.find(
      (candidate) => candidate.charAt(0).toUpperCase() + candidate.slice(1) === suffix // capitalize first letter to compare to a suffix
    );

    if (suffixCategory) {
      baseCategories = [prefixCategory, suffixCategory];
      break;
    }
  }

  // No bundle found means the category is a base category.
  if (!baseCategories.length) {
    return [category];
  }

  return baseCategories;
};

/**
 * Whether a category is a bundle, i.e. a joined category composed of more than one base category
 * (e.g. SEAT_WIFI, WIFI_MEAL, LOUNGE_WIFI). Base categories resolve to just themselves and are not bundles.
 */
export const isBundle = (category: Category | null | undefined): boolean =>
  getRelatedCategoriesForCategory(category).length > 1;

export const isSeatBundle = (category: Category | null | undefined): boolean => {
  const relatedCategories = getRelatedCategoriesForCategory(category);
  return relatedCategories.length > 1 && relatedCategories.includes(Category.SEAT);
};

/**
 * Get all overlapping categories for a given category. E.g. SEAT_MEAL => [MEAL, SEAT_WIFI, WIFI_MEAL, ...].
 * Optionally use `includeBundles` to omit bundle categories from the list.
 *
 * @param targetCategory - the category we look the overlaps for
 * @param includeBundles - if false only the atomic bases come back (seatWifi => [wifi]); if true also the
 *   sibling bundles sharing a base come along (seatWifi => [wifi, wifiMeal, ...])
 */
export const getOverlappingCategoriesForCategory = (targetCategory: Category, includeBundles = true): Category[] => {
  const baseCategories = getRelatedCategoriesForCategory(targetCategory);
  return ALL_CATEGORIES.filter((category) => {
    if (category === targetCategory) {
      return false;
    }

    const categoryBases = getRelatedCategoriesForCategory(category);
    const sharesBase = categoryBases.some((base) => baseCategories.includes(base));
    // A bundle has more than one base, so the caller can drop those when only atomic ones are wanted.
    const isBundle = categoryBases.length > 1;

    return sharesBase && (includeBundles || !isBundle);
  });
};

/**
 * The two individual ancillary categories each ancillary+ancillary bundle is composed of. Order is the
 * display order of the two service cards. Kept as an explicit map because the category names don't
 * split cleanly (e.g. "loungeCabinBaggage" → lounge + cabinBaggage, not lounge + cabin + baggage).
 */
export const ANCILLARY_BUNDLE_CONSTITUENTS: Partial<Record<Category, [Category, Category]>> = {
  [Category.LOUNGE_CABIN_BAGGAGE]: [Category.LOUNGE, Category.CABIN_BAGGAGE],
  [Category.LOUNGE_WIFI]: [Category.LOUNGE, Category.WIFI],
  [Category.LOUNGE_PRIORITY]: [Category.LOUNGE, Category.PRIORITY],
  [Category.WIFI_MEAL]: [Category.WIFI, Category.MEAL],
};

export const getBundleConstituents = (category: Category): [Category, Category] | undefined =>
  ANCILLARY_BUNDLE_CONSTITUENTS[category];

/**
 * localStorage key (opt-in set via /devtool → Bundles) that controls whether seat bundles in travel
 * extras are sourced from the cart catalog instead of the seatmap. When the value is missing or not
 * `'true'`, seat bundles keep coming from the seatmap (current live behaviour). Shared between the
 * devtool toggle that writes it and the service catalog selector consumer that reads it.
 */
export const SEAT_BUNDLES_FROM_CATALOG_STORAGE_KEY = 'seatBundlesFromCartCatalog';
